import { useMemo, useState } from 'react';
import type { ImportStatementResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney } from './money';
import {
  buildStatement,
  guessMapping,
  parseCsv,
  type AmountMode,
  type ColumnMapping,
} from './statement';
import { parseXlsx, XlsxError } from './xlsx';
import Hint from './Hint';

interface Props {
  companyId: string;
  bankAccountId: string;
  onImported: (result: ImportStatementResponse) => void;
  onCancel: () => void;
  onSessionLost: () => void;
}

const PREVIEW_ROWS = 5;

/**
 * استيراد كشف بنك من CSV أو XLSX: قراءة الملف فى المتصفح، ربط الأعمدة بتخمين من العناوين
 * يراجعه المستخدم، معاينة، ثم إرسال السطور الموحّدة. الخادم يتجاهل ما استُورد من قبل.
 */
export default function StatementImport({
  companyId,
  bankAccountId,
  onImported,
  onCancel,
  onSessionLost,
}: Props) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const columns = useMemo(() => {
    const width = Math.max(0, ...rows.map((row) => row.length));
    const header = mapping?.hasHeader ? (rows[0] ?? []) : [];
    return Array.from({ length: width }, (_, index) => header[index] || 'العمود ' + (index + 1));
  }, [rows, mapping?.hasHeader]);

  const built = useMemo(
    () => (mapping && rows.length > 0 ? buildStatement(rows, mapping) : null),
    [rows, mapping],
  );

  async function readFile(file: File): Promise<void> {
    setFailure(null);
    setIdempotencyKey(crypto.randomUUID());
    if (/\.xls$/i.test(file.name)) {
      setRows([]);
      setMapping(null);
      setFailure('صيغة Excel القديمة (xls) غير مدعومة — احفظ الكشف من Excel بصيغة xlsx أو CSV.');
      return;
    }
    let parsed: string[][];
    try {
      parsed = /\.xlsx$/i.test(file.name)
        ? await parseXlsx(await file.arrayBuffer())
        : parseCsv(await file.text());
    } catch (caught) {
      setRows([]);
      setMapping(null);
      setFailure(caught instanceof XlsxError ? caught.message : 'تعذّرت قراءة الملف.');
      return;
    }
    setFileName(file.name);
    setRows(parsed);
    setMapping(parsed[0] ? guessMapping(parsed[0]) : null);
    if (parsed.length === 0) setFailure('الملف فارغ.');
  }

  function update(change: Partial<ColumnMapping>): void {
    setMapping((current) => (current ? { ...current, ...change } : current));
    setIdempotencyKey(crypto.randomUUID());
  }

  async function submit(): Promise<void> {
    if (!built || built.lines.length === 0 || built.issues.length > 0) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const result = await api.importStatement(companyId, bankAccountId, idempotencyKey, {
        fileName: fileName || undefined,
        lines: built.lines,
      });
      onImported(result);
    } catch (caught) {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(
        caught instanceof ProblemError
          ? (caught.problem.detail ?? caught.problem.title)
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const columnSelect = (
    label: string,
    value: number | null,
    onChange: (value: number | null) => void,
    optional: boolean,
  ) => (
    <label>
      {label}
      <select
        value={value === null ? '' : String(value)}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      >
        {optional && <option value="">— لا يوجد —</option>}
        {columns.map((title, index) => (
          <option key={index} value={index}>
            {title}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>استيراد كشف بنك</h2>
        <button type="button" className="ghost" onClick={onCancel}>
          إغلاق
        </button>
      </div>
      <p className="sub">
        ملف Excel (xlsx) أو CSV كما يصدّره البنك — تُقرأ أول ورقة فى ملف Excel. الأرقام الهندية
        والفاصلة العربية مقبولة، والسطر المستورد من قبل يُتجاهل تلقائيًا.
      </p>
      <label className="file-field">
        <input
          type="file"
          accept=".csv,text/csv,.txt,.xlsx,.xls"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void readFile(file);
          }}
        />
      </label>

      {mapping && rows.length > 0 && (
        <>
          <h3>ربط الأعمدة</h3>
          <div className="form-row">
            <label className="check">
              <input
                type="checkbox"
                checked={mapping.hasHeader}
                onChange={(event) => update({ hasHeader: event.target.checked })}
              />
              الصف الأول عناوين
              <Hint>علّمه لو أول صف فى الملف أسماء أعمدة وليس أرقامًا.</Hint>
            </label>
            {columnSelect('التاريخ', mapping.date, (value) => update({ date: value ?? 0 }), false)}
            {columnSelect(
              'البيان',
              mapping.description,
              (value) => update({ description: value }),
              true,
            )}
            {columnSelect(
              'المرجع',
              mapping.reference,
              (value) => update({ reference: value }),
              true,
            )}
            {columnSelect('الرصيد', mapping.balance, (value) => update({ balance: value }), true)}
          </div>
          <div className="form-row">
            <label>
              أسلوب المبلغ
              <select
                value={mapping.mode}
                onChange={(event) => update({ mode: event.target.value as AmountMode })}
              >
                <option value="split">عمودان: سحب (مدين) وإيداع (دائن)</option>
                <option value="signed">عمود واحد بإشارة (السحب سالب)</option>
              </select>
              <Hint>
                كيف يكتب بنكك المبالغ: عمودان منفصلان للسحب والإيداع، أو عمود واحد بإشارة سالب.
              </Hint>
            </label>
            {mapping.mode === 'signed' ? (
              columnSelect('المبلغ', mapping.amount, (value) => update({ amount: value }), true)
            ) : (
              <>
                {columnSelect(
                  'سحب — مدين فى كشف البنك',
                  mapping.withdrawal,
                  (value) => update({ withdrawal: value }),
                  true,
                )}
                {columnSelect(
                  'إيداع — دائن فى كشف البنك',
                  mapping.deposit,
                  (value) => update({ deposit: value }),
                  true,
                )}
              </>
            )}
          </div>

          {built && (
            <>
              <p className="sub">
                {built.lines.length} حركة جاهزة
                {built.skipped > 0
                  ? ' • ' + built.skipped + ' صف بلا حركة تُرك (عناوين، رصيد افتتاحى، إجماليات)'
                  : ''}
              </p>
              {built.issues.length > 0 && (
                <ul className="error">
                  {built.issues.slice(0, 10).map((issue) => (
                    <li key={issue.row}>
                      الصف {issue.row}: {issue.message}
                    </li>
                  ))}
                </ul>
              )}
              {built.lines.length > 0 && (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>التاريخ</th>
                        <th>البيان</th>
                        <th>المرجع</th>
                        <th>المبلغ</th>
                        <th>الرصيد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {built.lines.slice(0, PREVIEW_ROWS).map((line, index) => (
                        <tr key={index}>
                          <td className="nowrap">{line.date}</td>
                          <td>{line.description}</td>
                          <td className="mono">{line.reference ?? ''}</td>
                          <td className="num-cell">{formatMoney(line.amount)}</td>
                          <td className="num-cell">
                            {line.balance !== undefined ? formatMoney(line.balance) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {failure && <p className="error">{failure}</p>}
      {built && (
        <div className="form-actions">
          <button
            type="button"
            className="primary"
            disabled={submitting || built.lines.length === 0 || built.issues.length > 0}
            onClick={() => void submit()}
          >
            {submitting ? 'جارى الاستيراد…' : 'استيراد ' + built.lines.length + ' حركة'}
          </button>
          {built.issues.length > 0 && (
            <span className="label">صحّح الربط أو الملف حتى تختفى الأخطاء.</span>
          )}
        </div>
      )}
    </div>
  );
}
